/* eslint-disable complexity */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import { CategoryNavItem, SeasonNavItem } from './components/nav-items';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import {
    statComparisonDataTypeSelector,
    statComparisonNavSelector
} from 'store/modules/statspro/stat-comparison/selectors';
import {
    BenchmarkingLegend,
    BenchmarkingExplainer
} from 'common/react/components/benchmarking';
import { DataTypeToggle, Heading, dataTypeOptions } from './components/header';
import { StatComparisonMain } from './components/StatComparisonMain';
import { playersSelector } from 'store/modules/statspro/player-comparison/selectors';
import classNames from 'classnames';
import { BenchmarkingPlaceholder } from 'common/react/components/benchmarking/BenchmarkingPlaceholder';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { useWindowSize } from 'common/react/hooks/use-window-resize';
import { initialiseNav } from 'common/store/modules/statspro/thunks/initialise-nav';

export const StatComparison = ({
    title,
    seasonId,
    competitionId,
    competitionPid,
    showBenchmarkingAflw
}) => {
    const dispatch = useDispatch();

    const nav = useSelector(statComparisonNavSelector);
    const dataType = useSelector(statComparisonDataTypeSelector);
    const options = useCompetitionNavigationOptions(nav);
    const playerIds = useSelector(playersSelector);
    const statsComparisonRef = useRef();

    useEffect(() => {
        dispatch(
            initialiseNav({
                slice: 'stat-comparison',
                initialValues: {
                    competitionId,
                    season: {
                        id: -1
                    }
                }
            })
        );
    }, [dispatch]);

    const showBenchmarkingAFLWBool = showBenchmarkingAflw === 'true';
    const { WOMENS } = PULSE.app.common.CONSTANTS.PROVIDER_IDS;
    const isAFLW = competitionPid === WOMENS;
    const showBenchmarking = !isAFLW || (isAFLW && showBenchmarkingAFLWBool);
    const windowSize = useWindowSize();
    const isResponsive = () =>
        windowSize.width <= PULSE.app.measurements.phablet;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );
    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    const isAFLM =
        competitionPid === PULSE.app.common.CONSTANTS.PROVIDER_IDS.PREMIERSHIP;

    const renderAveragesBenchmarking =
        showBenchmarking &&
        (dataType.type === 'averages' ||
            dataType.type === 'benchmarkedAverages');

    const renderTotalsBenchmarking =
        showBenchmarking &&
        (dataType.type === 'totals' || dataType.type === 'benchmarkedTotals');

    useFiltersTracking({
        title: `${title} filters`,
        filtersState: {
            category: nav.category,
            season: nav.season,
            dataType
        },
        options: {
            ...options,
            dataTypes: dataTypeOptions
        },
        filtersFields: ['category', 'career-season', 'dataType'],
        filtersRef: statsComparisonRef
    });

    return (
        <div
            className={classNames(
                'fade-in-on-load fade-in-on-load--stats widget--stats comparison-chart__wrapper',
                {
                    'is-loaded': playerIds[1] || playerIds[2]
                }
            )}
            ref={statsComparisonRef}
        >
            <div className="competition-nav filter-dropdown">
                {playerIds[1] || playerIds[2] ? (
                    <StatsSection.Main>
                        <StatsSection.Heading title={title} />
                        <StatsSection.Nav
                            showResponsiveItems={showResponsiveItems}
                            withScrollableFilter={!showResponsiveItems}
                        >
                            <StatsSection.NavItems>
                                <CategoryNavItem />
                                <SeasonNavItem />
                            </StatsSection.NavItems>
                        </StatsSection.Nav>
                        <StatsSection.Content>
                            <StatsSection.ContentHeader>
                                <StatsSection.ContentHeaderHeading>
                                    <Heading />
                                </StatsSection.ContentHeaderHeading>
                                <StatsSection.ContentHeaderOptions>
                                    <DataTypeToggle />
                                </StatsSection.ContentHeaderOptions>

                                <StatsSection.ContentHeaderBelow>
                                    {renderAveragesBenchmarking && (
                                        <BenchmarkingLegend />
                                    )}
                                    {renderAveragesBenchmarking && (
                                        <BenchmarkingExplainer />
                                    )}
                                </StatsSection.ContentHeaderBelow>

                                <StatsSection.ContentHeaderBelow
                                    isSingleModifier={true}
                                >
                                    {renderTotalsBenchmarking && (
                                        <BenchmarkingPlaceholder />
                                    )}
                                </StatsSection.ContentHeaderBelow>
                            </StatsSection.ContentHeader>
                            <StatsSection.ContentBody>
                                <StatComparisonMain
                                    playerIds={playerIds}
                                    seasonId={seasonId}
                                    isAFLW={isAFLW}
                                    isAFLM={isAFLM}
                                    showBenchmarking={showBenchmarking}
                                    compPid={competitionPid}
                                />
                            </StatsSection.ContentBody>
                        </StatsSection.Content>
                    </StatsSection.Main>
                ) : null}
            </div>
        </div>
    );
};

StatComparison.propTypes = {
    title: PropTypes.string.isRequired,
    seasonId: PropTypes.string.isRequired,
    compseasonId: PropTypes.string.isRequired,
    competitionId: PropTypes.string.isRequired,
    competitionPid: PropTypes.string.isRequired,
    showBenchmarkingAflw: PropTypes.string,
    showResponsiveItems: PropTypes.bool
};
